import { Business, AccountBalanceWalletOutlined } from '@mui/icons-material';
import { CardContainer, DataTable, SearchBar } from 'finara-components';
import type { DataTableColumn } from 'finara-components';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CsvExportButton } from '@/components/common';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { useClientList } from '@/hooks/queries/useSuperAdminClients';
import type { OrganizationClient } from '@/types/superAdminClients';
import { formatCurrency } from '@/utils/formatters';

export const OrganizationAccountsTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useClientList({ limit: 1000, orderDirection: 'ASC' });
  const { data: accountsData } = useAccounts({ type: 'organization', status: 'active', limit: 1000 });

  const clabeByOrgId = useMemo(() => {
    const map = new Map<string, string>();
    accountsData?.data.forEach(a => {
      if (a.organization_id) map.set(a.organization_id, a.clabe);
    });
    return map;
  }, [accountsData]);

  const clients = useMemo(() => {
    const all = data?.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter(c =>
      [c.name, c.code, clabeByOrgId.get(c.id)].some(field =>
        field?.toLowerCase().includes(term)
      )
    );
  }, [data, search, clabeByOrgId]);

  const totals = useMemo(() => ({
    saldoPrincipal: clients.reduce((sum, c) => sum + (c.saldoPrincipal ?? 0), 0),
    saldoGeneral: clients.reduce((sum, c) => sum + (c.saldoGeneral ?? 0), 0),
    saldoClientes: clients.reduce((sum, c) => sum + (c.saldoClientes ?? 0), 0),
  }), [clients]);

  const columns: DataTableColumn<OrganizationClient>[] = [
    {
      key: 'name',
      label: 'Organización',
      render: (_, client) => (
        <div className="flex items-center gap-2">
          <Business className="text-muted" fontSize="small" />
          <div>
            <div className="font-medium text-brand-white">{client.name}</div>
            <div className="text-xs text-muted">{client.code || client.id}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'clabe',
      label: 'CLABE',
      render: (_, client) => (
        <div className="font-mono text-sm text-brand-white">
          {clabeByOrgId.get(client.id) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'saldoPrincipal',
      label: 'Saldo Principal',
      align: 'right',
      render: (_, client) => (
        <div className="font-semibold text-brand-white">
          {formatCurrency(client.saldoPrincipal ?? 0)}
        </div>
      ),
    },
    {
      key: 'saldoGeneral',
      label: 'Saldo General',
      align: 'right',
      render: (_, client) => (
        <div className="font-semibold text-brand-white">
          {formatCurrency(client.saldoGeneral ?? 0)}
        </div>
      ),
    },
    {
      key: 'saldoClientes',
      label: 'Saldo Clientes',
      align: 'right',
      render: (_, client) => (
        <div className="font-semibold text-brand-white">
          {formatCurrency(client.saldoClientes ?? 0)}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      align: 'center',
      render: (_, client) => {
        const isActive = client.status === 'Activo';
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              isActive
                ? 'bg-green-500/20 text-green-400'
                : 'bg-black/5 dark:bg-white/5 text-brand-white'
            }`}
          >
            {client.status}
          </span>
        );
      },
    },
  ];

  return (
    <CardContainer>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AccountBalanceWalletOutlined className="text-muted" />
              <h3 className="text-lg font-semibold text-brand-white">
                Cuentas por Organización
              </h3>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted">
              <span>{clients.length} organizaciones</span>
              <span>•</span>
              <span className="font-semibold text-brand-white">
                Saldo General Total: {formatCurrency(totals.saldoGeneral)}
              </span>
            </div>
          </div>
          <CsvExportButton
            data={clients}
            filename="cuentas_organizaciones"
            columns={client => ({
              Organización: client.name,
              Código: client.code || '',
              CLABE: clabeByOrgId.get(client.id) || '',
              'Saldo Principal': formatCurrency(client.saldoPrincipal ?? 0).replace(/\s/g, ''),
              'Saldo General': formatCurrency(client.saldoGeneral ?? 0).replace(/\s/g, ''),
              'Saldo Clientes': formatCurrency(client.saldoClientes ?? 0).replace(/\s/g, ''),
              Estado: client.status,
            })}
            variant="white"
            size="small"
            buttonText="Exportar"
          />
        </div>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <SearchBar
            className="w-full"
            placeholder="Buscar por organización, código o CLABE..."
            value={search}
            onChange={setSearch}
          />
        </div>
        <DataTable
          columns={columns}
          data={clients}
          loading={isLoading}
          responsive
          onRowClick={client => void navigate(`/clientes/${client.id}`)}
          emptyMessage={
            <div className="py-8 text-center">
              <AccountBalanceWalletOutlined
                className="text-muted mx-auto mb-4"
                style={{ fontSize: '3rem' }}
              />
              <h3 className="text-lg font-medium text-brand-white mb-2">
                No se encontraron organizaciones
              </h3>
              <p className="text-muted">
                No hay cuentas de organizaciones disponibles en este momento.
              </p>
            </div>
          }
        />
      </div>
    </CardContainer>
  );
};
